// React
import { ChangeEvent, useRef, useState } from 'react';

// Uploady
import { BatchItem, FILE_STATES } from '@rpldy/chunked-uploady';

// Constant
import { ANOTHER_FILE_STATES, RAR, FORMAT_BYTES, FILE_SIZE_2_MB } from 'constant/file.constant';
import { messageUtils_error } from 'utils/common/message.utils';

// Interfaces
import { IUserUploaderProps } from './interfaces';

// i18n
import { useTranslation } from 'react-i18next';

// Utils
import { fileUtils_getDimensions } from 'utils/common/file.utils';

const useUploader = ({
  acceptedExtensions,
  maximumFileSize,
  onFinish,
  isOnlyMinimumDimensions,
  maxDimensions,
}: IUserUploaderProps) => {
  const { t } = useTranslation();
  const uploader_singleFileInputRef = useRef<HTMLInputElement>(null);
  const [uploadedFiles, setUploadedFiles] = useState<BatchItem[]>([]);
  const [uploader_imageId, uploader_setImageId] = useState<number | null>(null);

  /**
   * @description Click the single file ref
   *
   * @return {void} void
   */
  const uploader_onClickSingleFile = (currentImageId?: any): void => {
    if (uploader_singleFileInputRef.current) {
      if (currentImageId) uploader_setImageId(currentImageId || null);

      uploader_singleFileInputRef.current.click();
    }
  };

  /**
   * @description Watch any change in file input
   *
   * @param {ChangeEvent<HTMLInputElement>} event
   * @param {function} callback
   *
   * @return {void} void
   */
  const uploader_onChangeSingleFile = async (
    event: ChangeEvent<HTMLInputElement>,
    callback: (file: File) => void,
  ): Promise<void> => {
    const singleFile = event.target?.files?.[0] as File;
    const singleFileSize = maximumFileSize || FILE_SIZE_2_MB;
    const isRarFormat = singleFile?.name?.includes('.rar');
    const isRar = acceptedExtensions?.includes(RAR) && isRarFormat;

    const checkSize = (): boolean => {
      // Check for rar size
      if (singleFile && singleFile.size >= singleFileSize) {
        messageUtils_error(`${singleFile.name} ${t('app.validations.fileTooBig')} ${FORMAT_BYTES(singleFileSize || 0)}`);
        return false;
      } else {
        return true;
      }
    };

    // Reset the picker value
    if (uploader_singleFileInputRef?.current?.value) {
      uploader_singleFileInputRef.current.value = null as any;
    }

    if (checkSize()) {
      try {
        // Check for file dimensions only after maximum
        if (singleFile && isOnlyMinimumDimensions) {
          const dimensions = (await fileUtils_getDimensions(singleFile)) as { width: number; height: number };

          if (dimensions?.width < maxDimensions?.width! || dimensions?.height < maxDimensions?.height!) {
            messageUtils_error(
              `${t(`app.validations.files.minimumWidth`)} ${maxDimensions?.width || 0}px ${t('app.and')} ${t(
                'app.validations.files.minimumHeight',
              )} ${maxDimensions?.height || 0}px`,
            );

            return;
          }
        }

        // Check for rar or * (all) extension
        if (isRar || acceptedExtensions?.includes('*')) {
          // Force the callback to be called
          callback(singleFile);

          return;
        }

        // Check the format for all extensions, except .rar and * (all)
        let validationFlag1: boolean = true;
        let validationFlag2: boolean = true;

        if (singleFile && !acceptedExtensions?.includes(singleFile.type) && !isRar) {
          validationFlag1 = false;
          if (!acceptedExtensions?.includes(`.${singleFile.type.split('/')[1]}`)) {
            validationFlag2 = false;
            if (singleFile.type === 'application/msword' && acceptedExtensions?.includes(`.doc`)) {
              validationFlag2 = true;
            }
          }
          // if (singleFile.type == 'image/jpeg') {
          //   validationFlag2 = false;
          //   console.log(singleFile.name.split('.'));
          //   if (singleFile.name.split('.')[1] == 'jpg') {
          //     validationFlag2 = true;
          //   } else if (singleFile.name.split('.')[1] == 'jpeg') {
          //     validationFlag2 = true;
          //   }
          // }
        }
        if (!validationFlag1 && !validationFlag2) {
          messageUtils_error(`${singleFile.name} ${t('app.validations.notValid')}`);
          return;
        }

        callback(singleFile);
      } catch (err) {
        if (typeof err === 'string') {
          messageUtils_error(t(err));
        }
      }
    }
  };

  /**
   * @description Watch any change in file uploader
   *
   * @return {Promise<void>} Promise<void>
   */
  const uploader_onChangeFile = async (state: FILE_STATES | ANOTHER_FILE_STATES, item?: BatchItem): Promise<void> => {
    if (item) {
      const findExistedFile = uploadedFiles.find(uploadedFile => uploadedFile.id === item.id);
      if (findExistedFile)
        setUploadedFiles(previousUploadedFiles => previousUploadedFiles.map(file => (file.id === item.id ? item : file)));
      else setUploadedFiles(previousUploadedFiles => [...previousUploadedFiles, item]);

      switch (state) {
        case ANOTHER_FILE_STATES.DELETE:
          setUploadedFiles(previousUploadedFiles => previousUploadedFiles.filter(file => file.id !== item.id));
          break;
        case FILE_STATES.FINISHED:
          onFinish && onFinish(item);
          break;
        default:
          return;
      }
    }
  };

  /**
   * @description Clear uploaded files
   *
   * @return {void} void
   */
  const uploader_clearUploadedFiles = (): void => {
    setUploadedFiles([]);
  };

  /**
   * @description Use input
   *
   * @param {callback} onChange
   *
   * @return {void} void
   */
  const uploader_Input = (onChange: (file: File) => void): JSX.Element => {
    return (
      <input
        type="file"
        style={{ display: 'none' }}
        accept={acceptedExtensions?.toString()}
        ref={uploader_singleFileInputRef}
        onChange={(event: ChangeEvent<HTMLInputElement>) => uploader_onChangeSingleFile(event, file => onChange(file))}
      />
    );
  };

  return {
    uploadedFiles,
    uploader_imageId,
    uploader_singleFileInputRef,
    uploader_onChangeFile,
    uploader_clearUploadedFiles,
    uploader_onChangeSingleFile,
    uploader_onClickSingleFile,
    uploader_Input,
  };
};

export { useUploader };
